import { useState, Fragment } from 'react'
import { useTranslation } from '../../i18n/LanguageContext'
import { useTimer } from '../useTimer'
import { useMaths } from './useMaths'
import { type Operation, type Difficulty, OPERATION_ICONS, OPERATION_EMOJIS } from './MathsData'
import GameSetupScreen from '../GameSetupScreen'
import SaveErrorBanner from '../SaveErrorBanner'
import { ss } from '../gameSetupStyles'
import GamePlayHeader from '../GamePlayHeader'
import { useSessionMode } from '../../professional/SessionModeContext'
import { useGameCompletion } from '../../hooks/useGameCompletion'
import { useGameSession } from '../../hooks/useGameSession'
import { useActivitySession } from '../../hooks/useActivitySession'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { COLORS } from '../../styles/tokens'

interface MathsConfig { activeOps: Operation[]; difficulty: Difficulty }

interface MathsGameProps {
  onBack: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const ALL_OPERATIONS: Operation[] = ['addition', 'subtraction', 'multiplication', 'division']
const DIFFICULTIES: Difficulty[] = ['easy', 'medium', 'hard']

export default function MathsGame({ onBack }: MathsGameProps) {
  const { t } = useTranslation()
  const sessionMode = useSessionMode()
  const { savedSession, saveConfig, clear } = useActivitySession<MathsConfig>('maths', !sessionMode)
  const restored = savedSession?.config ?? null

  const [screen, setScreen] = useState<GameScreen>(sessionMode ? 'playing' : (restored ? 'playing' : 'setup'))
  const [selectedOps, setSelectedOps] = useState<Operation[]>(
    (sessionMode?.config.activeOps as Operation[] | undefined) ?? restored?.activeOps ?? ['addition']
  )
  const [difficulty, setDifficulty] = useState<Difficulty>(
    (sessionMode?.config.difficulty as Difficulty) ?? restored?.difficulty ?? 'easy'
  )

  const activeOps: Operation[] = selectedOps.length > 0 ? selectedOps : ['addition']
  const { state, resetGame, answerQuestion } = useMaths(activeOps, difficulty)

  const { gameComplete, moves, startTime, endTime, correctCount, questions } = state

  const { sessionId } = useGameSession({
    activitySlug: 'maths',
    difficulty,
    enabled: screen === 'playing',
  })

  const { saveError, clearSaveError } = useGameCompletion({
    gameComplete,
    activitySlug:  'maths',
    difficulty,
    moves,
    startTime,
    endTime,
    pairsTotal:   questions.length,
    pairsMatched: correctCount,
    sessionId,
    onCompleted:  () => { clear(); setScreen('complete') },
  })

  const handleStart = () => { resetGame(); saveConfig({ activeOps, difficulty }); setScreen('playing') }

  const toggleOperation = (op: Operation) => {
    setSelectedOps((prev) => {
      if (prev.includes(op)) {
        if (prev.length === 1) return prev   // always keep at least one
        return prev.filter((o) => o !== op)
      }
      return [...prev, op]
    })
  }

  return (
    <div style={s.root}>
      {saveError && <SaveErrorBanner message={t.common.saveError} dismissLabel={t.common.dismissError} onDismiss={clearSaveError} />}
      {screen === 'setup' && (
        <SetupScreen
          selectedOps={activeOps}
          difficulty={difficulty}
          onToggleOp={toggleOperation}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}
      {screen === 'playing' && (
        <PlayScreen
          state={state}
          onAnswer={answerQuestion}
          onBack={sessionMode ? onBack : () => { clear(); setScreen('setup') }}
        />
      )}
      {screen === 'complete' && (
        <GameCompleteScreen
          emoji="🧮"
          title={t.maths.complete.title}
          subtitle={t.maths.complete.subtitle}
          stats={[
            { value: `${state.correctCount}/${state.questions.length}`, label: t.maths.complete.correctLabel },
            { value: formatGameTime(state.startTime, state.endTime), label: t.maths.complete.timeLabel },
            { value: t.maths.difficulty[difficulty].label, label: t.maths.complete.levelLabel },
          ]}
          playAgainLabel={t.maths.complete.playAgain}
          changeLevelLabel={t.maths.complete.changeLevel}
          backLabel={t.maths.complete.backToDashboard}
          onPlayAgain={() => { resetGame(); saveConfig({ activeOps, difficulty }); setScreen('playing') }}
          onChangeLevel={() => { clear(); setScreen('setup') }}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ─────────────────────────────────────────────── */
interface SetupScreenProps {
  selectedOps: Operation[]
  difficulty: Difficulty
  onToggleOp: (op: Operation) => void
  onDifficultyChange: (d: Difficulty) => void
  onStart: () => void
  onBack: () => void
}

function SetupScreen({ selectedOps, difficulty, onToggleOp, onDifficultyChange, onStart, onBack }: SetupScreenProps) {
  const { t } = useTranslation()
  const m = t.maths

  return (
    <GameSetupScreen
      title={m.title}
      subtitle={m.subtitle}
      backLabel={m.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...m.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as Difficulty)}
      chooseDifficultyLabel={m.chooseDifficultyTitle}
      startLabel={m.startBtn}
      onStart={onStart}
      preview={<PreviewIllustration ops={selectedOps} />}
    >
      {/* Operation selector */}
      <section>
        <h2 style={ss.optionLabel}>{m.chooseOpsTitle}</h2>
        <div style={s.opsGrid}>
          {ALL_OPERATIONS.map((op) => {
            const active = selectedOps.includes(op)
            return (
              <button
                key={op}
                onClick={() => onToggleOp(op)}
                style={{ ...ss.modeCard, ...(active ? ss.modeCardActive : {}) }}
                aria-pressed={active}
              >
                <span style={s.opSymbol}>{OPERATION_EMOJIS[op]}</span>
                <span style={s.opLabel}>{m.operations[op]}</span>
                <span style={s.opSign}>{OPERATION_ICONS[op]}</span>
              </button>
            )
          })}
        </div>
        <p style={s.opsHint}>{m.chooseOpsHint}</p>
      </section>
    </GameSetupScreen>
  )
}

function PreviewIllustration({ ops }: { ops: Operation[] }) {
  return (
    <div style={s.preview}>
      <div style={s.previewCard}>
        <span style={s.previewA}>7</span>
        <span style={s.previewOp}>{OPERATION_ICONS[ops[0]]}</span>
        <span style={s.previewB}>3</span>
        <span style={s.previewEquals}>=</span>
        <span style={s.previewAnswer}>?</span>
      </div>
      <div style={s.previewChoices}>
        {[10, 4, 21, 73].map((n) => (
          <span key={n} style={s.previewChoice}>{n}</span>
        ))}
      </div>
    </div>
  )
}

/* ── Play Screen ─────────────────────────────────────────────── */
interface PlayScreenProps {
  state: ReturnType<typeof useMaths>['state']
  onAnswer: (choice: number) => void
  onBack: () => void
}

function PlayScreen({ state, onAnswer, onBack }: PlayScreenProps) {
  const { t } = useTranslation()
  const m = t.maths
  const timeDisplay = useTimer(state.startTime, state.endTime)
  const { questions, currentIndex, wrongChoice, correctFlash } = state
  const question = questions[currentIndex]
  const progress = (currentIndex / questions.length) * 100 +
    (correctFlash ? (1 / questions.length) * 100 : 0)

  // 4-operand equations need scaled-down sizing to fit portrait screens
  const isLong = question.operands.length >= 4
  const longCard     = isLong ? { gap: 8, padding: '24px 12px', flexWrap: 'wrap' as const } : {}
  const longOperand  = isLong ? { fontSize: 'clamp(1.4rem, 5.5vw, 2.8rem)' } : {}
  const longSymbol   = isLong ? { fontSize: 'clamp(1.2rem, 4.5vw, 2.2rem)', minWidth: 28 } : {}
  const longQuestion = isLong ? { fontSize: 'clamp(1.4rem, 5.5vw, 2.8rem)', minWidth: 56 } : {}

  return (
    <div style={s.playRoot}>
      <GamePlayHeader
        title={m.title}
        subtitle={m.questionCounter
          .replace('{n}', String(currentIndex + 1))
          .replace('{total}', String(questions.length))}
        stats={[
          { value: state.correctCount, label: m.correct },
          { value: timeDisplay,        label: m.time },
        ]}
        progress={progress}
        progressNow={currentIndex}
        progressMax={questions.length}
        onBack={onBack}
        backLabel={m.backToDashboard}
      />

      <div style={s.playBody}>
        {/* Equation display */}
        <div style={{ ...s.equationCard, ...(correctFlash ? s.equationCardCorrect : {}), ...longCard }}>
          {question.operands.map((operand, i) => (
            <Fragment key={i}>
              {i > 0 && <span style={{ ...s.eqOp, ...longSymbol }}>{OPERATION_ICONS[question.operation]}</span>}
              <span style={{ ...s.eqOperand, ...longOperand }}>{operand}</span>
            </Fragment>
          ))}
          <span style={{ ...s.eqEquals, ...longSymbol }}>=</span>
          <span style={{ ...s.eqQuestion, ...longQuestion }}>?</span>
        </div>

        <p style={s.instruction}>
          <em>{correctFlash ? m.instructionCorrect : m.instructionPick}</em>
        </p>

        {/* Answer choices */}
        <div style={s.choicesGrid}>
          {question.choices.map((choice) => {
            const isWrong   = wrongChoice === choice
            const isCorrect = correctFlash && choice === question.answer
            return (
              <button
                key={choice}
                onClick={() => onAnswer(choice)}
                disabled={correctFlash || wrongChoice !== null}
                className={isWrong ? 'wm-shake' : ''}
                style={{
                  ...s.choiceBtn,
                  ...(isWrong   ? s.choiceBtnWrong   : {}),
                  ...(isCorrect ? s.choiceBtnCorrect : {}),
                }}
                aria-label={String(choice)}
              >
                {choice}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/* ── Styles ──────────────────────────────────────────────────── */
const s: Record<string, React.CSSProperties> = {
  root: { minHeight: '100vh' },

  opsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 14,
  },
  opSymbol: { fontSize: '1.8rem', lineHeight: 1 },
  opLabel: { fontSize: '1rem', fontWeight: 700, color: '#2E1F14' },
  opSign: {
    fontSize: '1.4rem',
    fontWeight: 900,
    color: COLORS.green,
    fontFamily: 'monospace',
  },
  opsHint: { marginTop: 10, fontSize: '0.88rem', color: '#9A8070', fontStyle: 'italic' },

  /* Preview */
  preview: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 14,
    padding: '24px',
    borderRadius: 18,
    background: COLORS.bgPage,
    border: `1.5px solid ${COLORS.borderWarm}`,
  },
  previewCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    fontSize: '2.4rem',
    fontWeight: 900,
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    color: COLORS.greenDark,
  },
  previewA:      { color: COLORS.greenDark },
  previewOp:     { color: COLORS.green, fontSize: '1.8rem' },
  previewB:      { color: COLORS.greenDark },
  previewEquals: { color: '#9A8070', fontSize: '1.8rem' },
  previewAnswer: {
    color: '#FFFFFF',
    background: COLORS.green,
    borderRadius: 10,
    padding: '2px 14px',
    fontSize: '2rem',
  },
  previewChoices: { display: 'flex', gap: 12 },
  previewChoice: {
    padding: '10px 18px',
    borderRadius: 10,
    background: '#FFFFFF',
    border: '2px solid #D8C9B8',
    fontSize: '1.2rem',
    fontWeight: 700,
    color: '#2E1F14',
  },

  /* Play */
  playRoot: { minHeight: '100vh', display: 'flex', flexDirection: 'column' },

  playBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '32px 24px 48px',
    gap: 28,
    maxWidth: 600,
    margin: '0 auto',
    width: '100%',
  },

  equationCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 18,
    padding: '32px 40px',
    borderRadius: 24,
    background: '#FFFFFF',
    border: `2px solid ${COLORS.borderWarm}`,
    boxShadow: '0 8px 32px rgba(27,67,50,0.10)',
    width: '100%',
    transition: 'background 0.25s ease, border-color 0.25s ease',
  },
  equationCardCorrect: {
    background: '#F0FAF4',
    borderColor: COLORS.greenLight,
    boxShadow: '0 8px 32px rgba(82,183,136,0.18)',
  },
  eqOperand: {
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    fontSize: 'clamp(2.4rem, 8vw, 4rem)',
    fontWeight: 700,
    color: COLORS.greenDark,
    lineHeight: 1,
  },
  eqOp: {
    fontFamily: 'monospace',
    fontSize: 'clamp(1.8rem, 6vw, 2.8rem)',
    fontWeight: 900,
    color: COLORS.green,
    lineHeight: 1,
    minWidth: 40,
    textAlign: 'center' as const,
  },
  eqEquals: {
    fontFamily: 'monospace',
    fontSize: 'clamp(1.8rem, 6vw, 2.8rem)',
    color: '#9A8070',
    lineHeight: 1,
  },
  eqQuestion: {
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    fontSize: 'clamp(2.4rem, 8vw, 4rem)',
    fontWeight: 700,
    color: '#FFFFFF',
    background: COLORS.green,
    borderRadius: 12,
    padding: '0 20px',
    lineHeight: 1.2,
    minWidth: 72,
    textAlign: 'center' as const,
  },

  instruction: { fontSize: '1rem', color: '#6B4C3B', fontWeight: 500, textAlign: 'center' as const },

  choicesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 16,
    width: '100%',
  },
  choiceBtn: {
    padding: '22px 16px',
    fontSize: 'clamp(2rem, 8vw, 3.5rem)',
    fontWeight: 800,
    fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif",
    color: COLORS.greenDark,
    background: '#FFFFFF',
    border: `2px solid ${COLORS.borderWarm}`,
    borderRadius: 16,
    boxShadow: '0 3px 12px rgba(27,67,50,0.08)',
    cursor: 'pointer',
    transition: 'all 0.12s ease',
    minHeight: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  choiceBtnWrong: {
    background: '#FFF0F0',
    border: '2.5px solid #E53E3E',
    color: '#C53030',
  },
  choiceBtnCorrect: {
    background: '#F0FAF4',
    border: `2.5px solid ${COLORS.greenLight}`,
    color: COLORS.greenDark,
  },

}
